'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface LicensePlateInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  country?: 'S' | 'DK' | 'N' | 'FIN';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// EU stars in a circle pattern (12 stars)
function EUStars({ className }: { className?: string }) {
  const stars = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180); // Start from top, 30° apart
    const cx = 12 + 8 * Math.cos(angle);
    const cy = 12 + 8 * Math.sin(angle);
    stars.push(
      <circle
        key={i}
        cx={cx}
        cy={cy}
        r="1.5"
        fill="#FFCC00"
      />
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
    >
      {stars}
    </svg>
  );
}

// Proper 5-pointed star shape for EU flag
function EUStarsProper({ className }: { className?: string }) {
  const createStar = (cx: number, cy: number, size: number) => {
    const points = [];
    for (let i = 0; i < 5; i++) {
      // Outer point
      const outerAngle = (i * 72 - 90) * (Math.PI / 180);
      points.push(`${cx + size * Math.cos(outerAngle)},${cy + size * Math.sin(outerAngle)}`);
      // Inner point
      const innerAngle = ((i * 72) + 36 - 90) * (Math.PI / 180);
      points.push(`${cx + (size * 0.4) * Math.cos(innerAngle)},${cy + (size * 0.4) * Math.sin(innerAngle)}`);
    }
    return points.join(' ');
  };

  const stars = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const cx = 20 + 14 * Math.cos(angle);
    const cy = 20 + 14 * Math.sin(angle);
    stars.push(
      <polygon
        key={i}
        points={createStar(cx, cy, 3)}
        fill="#FFCC00"
      />
    );
  }

  return (
    <svg
      viewBox="0 0 40 40"
      className={className}
      aria-hidden="true"
    >
      {stars}
    </svg>
  );
}

const sizeStyles = {
  sm: {
    container: 'h-10',
    blueSection: 'w-8',
    stars: 'w-5 h-5',
    country: 'text-[10px]',
    input: 'text-base px-3',
  },
  md: {
    container: 'h-12',
    blueSection: 'w-10',
    stars: 'w-6 h-6',
    country: 'text-xs',
    input: 'text-lg px-3',
  },
  lg: {
    container: 'h-14',
    blueSection: 'w-12',
    stars: 'w-7 h-7',
    country: 'text-sm',
    input: 'text-xl px-3',
  },
};

export function LicensePlateInput({
  value,
  onChange,
  onSubmit,
  placeholder = 'ABC 123',
  disabled = false,
  loading = false,
  country = 'S',
  size = 'md',
  className,
}: LicensePlateInputProps) {
  const styles = sizeStyles[size];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Auto-uppercase and remove spaces for internal value
    const newValue = e.target.value.toUpperCase();
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit && !disabled && !loading) {
      e.preventDefault();
      onSubmit();
    }
  };

  const isDisabled = disabled || loading;

  return (
    <div
      className={cn(
        'relative flex overflow-hidden rounded border-2 border-gray-400 shadow-sm',
        'bg-white',
        styles.container,
        isDisabled && 'opacity-60 cursor-not-allowed',
        className
      )}
    >
      {/* Blue EU section */}
      <div
        className={cn(
          'flex flex-col items-center justify-center',
          'bg-[#003399]',
          styles.blueSection
        )}
        style={{ backgroundColor: '#003399' }}
      >
        <EUStarsProper className={cn(styles.stars, 'mb-0.5')} />
        <span className={cn('text-white font-bold leading-none', styles.country)}>
          {country}
        </span>
      </div>

      {/* White input section */}
      <div className="relative flex-1 flex items-center bg-white">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled}
          maxLength={7}
          className={cn(
            'w-full h-full bg-transparent border-none outline-none',
            'font-mono font-bold tracking-[0.15em] uppercase text-center',
            'text-black placeholder:text-gray-400',
            'focus:outline-none',
            styles.input
          )}
          aria-label="Registreringsnummer"
        />

        {/* Loading spinner */}
        {loading && (
          <div className="absolute right-3 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        )}
      </div>
    </div>
  );
}
